// Development page — shared labels, cell wording and the ratio bar. Everything numeric comes from
// GET /api/gps/development/key-points (services/development/); nothing here computes a key point.
//
// Colour: the Trends page's direction tokens (TRENDS_CSS in trendsShared.js — validated for both
// themes), meaning only "above / below the group": blue above, orange below, grey inside the ±10%
// band. Never good or bad — the signed % is always shown next to it, in text ink.
import React from 'react';
import { METRIC_CATALOG } from './metricsCatalog';
import { METRICS, POSITION_ORDER, fmtPct, fmtDate } from './trendsShared';

export { METRICS, POSITION_ORDER, fmtPct, fmtDate };
export const BLOCKS = ['training', 'match'];
export const BLOCK_LABEL = { match: 'Match', training: 'Training' };
export const LONG_LABEL = Object.fromEntries(METRICS.map(m => [m.key, METRIC_CATALOG.find(c => c.key === m.key)?.label || m.label]));
export const LINE_LABEL = { DEF: 'defenders', MID: 'midfielders', ATT: 'attackers' };
export const NOT_PICKABLE_NOTE = 'never a key point — too little sprint volume in training';

export const DEV_CSS = `
.dev-root .dev-cell { border-left: 3px solid transparent; padding-left: 7px; }
.dev-root .dev-spec { border-left-color: var(--tr-up-line); }
.dev-root .dev-imp { border-left-color: var(--tr-down-line); }
.dev-root .dev-badge { display: inline-block; margin-left: 6px; font-size: 9.5px; font-weight: 700; padding: 1px 5px;
  border-radius: 4px; border: 1px solid var(--border-strong); color: var(--text-secondary); vertical-align: middle; letter-spacing: 0.3px; }
.dev-root .dev-btn { padding: 6px 12px; border-radius: 8px; border: 1px solid var(--border-strong); background: var(--bg-card);
  color: var(--text-secondary); font-size: 12px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; }
.dev-root .dev-btn:hover { background: var(--bg-row-hover); }
.dev-root .dev-btn:focus-visible { outline: 2px solid var(--tr-up-line); outline-offset: 2px; }
.dev-root .dev-barrow { background: none; border: none; padding: 1px 4px; margin: 0 -4px; border-radius: 6px; cursor: pointer; text-align: left; font: inherit; color: inherit; }
.dev-root .dev-barrow:hover { background: var(--bg-row-hover); }
.dev-root .dev-barrow:focus-visible, .dev-root .dev-linkbtn:focus-visible { outline: 2px solid var(--tr-up-line); outline-offset: 1px; }
.dev-root .dev-linkbtn { background: none; border: none; padding: 0; font: inherit; color: inherit; cursor: pointer;
  text-decoration: underline dotted; text-underline-offset: 3px; }
`;

const groupLabel = (block) => (block.referenceUsed === 'target' ? `the ${block.referenceName} match target`
  : block.referenceUsed === 'position' ? `position group ${block.referenceName}`
  : block.referenceUsed === 'line' ? `the ${LINE_LABEL[block.referenceName] || block.referenceName}`
  : 'the squad (outfield players)');

// "vs the defenders: Kuipers (CB), Mesik (FB), Bakker (FB)" / "vs the CB match target"
export function referenceText(block) {
  if (!block.referenceUsed) return null;
  if (block.referenceUsed === 'target') return `vs ${groupLabel(block)}`;
  const names = (block.referencePlayers || []).map(p => `${p.name} (${p.position})`).join(', ');
  return `vs ${groupLabel(block)}${names ? `: ${names}` : ''}`;
}

// Badge for a block compared with something wider than the position group.
export function referenceBadge(block) {
  if (block.referenceUsed === 'line') {
    return { label: 'LINE', title: `Position group too small — compared with ${groupLabel(block)} (${(block.referencePlayers || []).length + 1} players)` };
  }
  if (block.referenceUsed === 'squad') return { label: 'SQUAD', title: 'Position group too small — compared with the squad' };
  return null;
}

// Short reason for a block without key points.
export function stateShort(blockKey, block) {
  if (blockKey === 'match') return `too few matches (n = ${block.nValid})`;
  if (block.state === 'rehab') return `rehab (${block.rehabScenarios} of ${block.nScenarios} scenarios)`;
  if (block.state === 'insufficient') return `too few scenarios (n = ${block.nValid})`;
  return 'no reference group';
}

// Match vs target: Match Performance's format — % of target, no decimals ("84%").
export const fmtOfTarget = v => (v == null || isNaN(v) ? '—' : `${Math.round(v)}%`);
// The % next to a metric: % of target (match vs target) or signed % vs the group (training).
export const relText = mm => (mm.pctOfTarget != null ? fmtOfTarget(mm.pctOfTarget) : fmtPct(mm.pctVsGroup));

// Short cell text for one key point: "Endurance – RD −21%" / "High speed – SD 84% of target" / "No standout".
export function keyPointShort(kp) {
  if (!kp) return '—';
  if (kp.kind === 'none') return 'No standout';
  const label = METRICS.find(m => m.key === kp.metric)?.label || kp.metric;
  if (kp.pctOfTarget != null) return `${kp.family} – ${label} ${fmtOfTarget(kp.pctOfTarget)} of target`;
  return `${kp.family} – ${label} ${fmtPct(kp.pctVsGroup)}`;
}

// Compact two-line form for the table: { main: "SD 84%" / "HID −26%", family: "High speed" } or
// null for "No standout". Keeps the 8-column table within the page width.
export function keyPointCompact(kp) {
  if (!kp || kp.kind === 'none') return null;
  const label = METRICS.find(m => m.key === kp.metric)?.label || kp.metric;
  const value = kp.pctOfTarget != null ? fmtOfTarget(kp.pctOfTarget) : fmtPct(kp.pctVsGroup);
  return { main: `${label} ${value}`, family: kp.family };
}

// The match block's average % of target per metric, as the table / cards / PDF show it (TD, HID,
// SD — Match Performance's TD% / HID% / SD%). Shown from 1 valid match; below the key-point minimum
// it's marked `few` (muted, with n).
export const PCT_METRICS = [{ key: 'td', label: 'TD' }, { key: 'hid', label: 'HID' }, { key: 'sd', label: 'SD' }];
export const MIN_MATCHES_FOR_KEY_POINT = 4; // mirrors config/developmentKeyPoints.js MIN_MATCHES, display only
export function matchPct(block, metricKey) {
  const mm = block && block.metrics && block.metrics[metricKey];
  if (!mm || mm.pctOfTarget == null) return { text: '—', n: mm ? mm.n : 0, few: false, none: true, friendly: false };
  return { text: fmtOfTarget(mm.pctOfTarget), n: mm.n, few: (mm.n ?? 0) < MIN_MATCHES_FOR_KEY_POINT, none: false, friendly: (block.nFriendly || 0) > 0 };
}

// "friendly vs Telstar (18-08)" for the included friendlies of the period (meta.matchesIncluded),
// or null when none is included. The session title carries the word "Friendly" — dropped here.
export function friendlyLabel(meta) {
  const f = ((meta && meta.matchesIncluded) || []).filter(m => m.friendly);
  if (!f.length) return null;
  const one = m => `${String(m.opponent || 'unknown').replace(/\s*friendly\s*/i, '').trim()} (${fmtDate(m.date).slice(0, 5)})`;
  return `friendl${f.length === 1 ? 'y' : 'ies'} vs ${f.map(one).join(', ')}`;
}

export function fmtValue(blockKey, metricKey, v) {
  if (v == null || isNaN(v)) return '—';
  if (blockKey === 'training') return `${v.toFixed(2)}×`;
  const unit = METRICS.find(m => m.key === metricKey)?.unit;
  return `${Math.round(v).toLocaleString('en-GB')}${unit ? ` ${unit}` : ''}`;
}

// ── Ratio bar ──────────────────────────────────────────────────────────────────────────────────
// Diverging from 1.00 (the group median). Domain [lo, hi] is shared by every bar in the drawer so
// lengths compare across metrics and blocks.
export function ratioDomain(ratios) {
  const vals = ratios.filter(r => r != null && isFinite(r));
  const lo = Math.max(0.25, Math.min(0.5, ...vals.map(r => Math.floor(r * 4) / 4)));
  const hi = Math.min(2.5, Math.max(1.5, ...vals.map(r => Math.ceil(r * 4) / 4)));
  return [lo, hi];
}

const BAND = { lo: 0.9, hi: 1.1 };

// mode 'target': the 1.00 line is the target and ticks read as % of target ("125%"); otherwise
// the line is the group median and ticks read as ± % ("+25%").
export function RatioAxis({ domain, mode }) {
  const [lo, hi] = domain;
  const x = r => `${((r - lo) / (hi - lo)) * 100}%`;
  const ticks = [];
  for (let t = Math.ceil(lo * 4) / 4; t <= hi + 1e-9; t += 0.25) ticks.push(Math.round(t * 100) / 100);
  return (
    <div aria-hidden="true" style={{ position: 'relative', height: 14, fontSize: 10, color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
      {ticks.filter((t, i) => t === 1 || ticks.length <= 7 || i % 2 === 0).map(t => (
        <span key={t} style={{ position: 'absolute', left: x(t), transform: 'translateX(-50%)', whiteSpace: 'nowrap' }}>
          {t === 1 ? (mode === 'target' ? 'target' : 'median') : mode === 'target' ? fmtOfTarget(t * 100) : fmtPct((t - 1) * 100)}
        </span>
      ))}
    </div>
  );
}

// One metric row's bar. Clamped bars end in a small notch so a cut-off is visible.
export function RatioBar({ ratio, domain, title }) {
  const [lo, hi] = domain;
  const pos = r => ((Math.min(hi, Math.max(lo, r)) - lo) / (hi - lo)) * 100;
  const base = pos(1);
  const has = ratio != null && isFinite(ratio);
  const end = has ? pos(ratio) : base;
  const left = Math.min(base, end), width = Math.abs(end - base);
  const above = has && ratio > 1;
  // Strictly inside: exactly 0.90 / 1.10 already qualify as a key point.
  const inBand = has && ratio > BAND.lo + 1e-9 && ratio < BAND.hi - 1e-9;
  const color = !has ? 'transparent' : inBand ? 'var(--tr-stable)' : above ? 'var(--tr-up-line)' : 'var(--tr-down-line)';
  const clipped = has && (ratio > hi || ratio < lo);
  return (
    <div title={title} style={{ position: 'relative', height: 18 }}>
      <div style={{ position: 'absolute', top: 2, bottom: 2, left: `${pos(BAND.lo)}%`, width: `${pos(BAND.hi) - pos(BAND.lo)}%`, background: 'var(--tr-band)', borderRadius: 2 }} />
      <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${base}%`, borderLeft: '1px dashed var(--tr-ref)' }} />
      {has && (
        <div style={{ position: 'absolute', top: 5, height: 8, left: `${left}%`, width: `${Math.max(width, 0.6)}%`, background: color,
          borderRadius: above ? '0 4px 4px 0' : '4px 0 0 4px',
          ...(clipped ? { [above ? 'borderRight' : 'borderLeft']: '2px solid var(--bg-card)' } : {}) }} />
      )}
    </div>
  );
}
